/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';
import { Combobox } from '@base-ui/react/combobox';
import { ScrollArea } from '@base-ui/react/scroll-area';
import { Button } from '@base-ui/react/button';

import { UIcon } from 'shared-ui/uicon';

import { useSearchSuggestionsQuery } from '../../api/search.queries';
import type { SearchSuggestionGroup, SearchSuggestionItem } from '../../api/search.types';
import { useFiltersState } from '../../FiltersContext';
import { IconBadge } from '../../IconBadge/IconBadge';

import { useEmptySearchContents } from './useEmptySearchContents';
import { useRecentLocationSearches } from './useRecentLocationSearches';

import styles from './LocationsCombobox.module.scss';
import sharedStyles from '../sharedStyles.module.scss';

interface LocationsComboboxProps {
    collapseAutocomplete: (options?: { shouldFocusTrigger?: boolean }) => void;
}

function trackHomepageMapEvent(action: string) {
    window.dataLayer?.push({
        event: 'map_interaction',
        category: 'Homepage',
        action,
        label: 'Homepage Map',
    });
}

export function LocationsCombobox({ collapseAutocomplete }: LocationsComboboxProps) {
    const emptySearchContents = useEmptySearchContents();
    const { addRecentSearch } = useRecentLocationSearches();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const popupRef = React.useRef<HTMLDivElement>(null);
    const [, setFilters] = useFiltersState();
    const [selectedValue, setSelectedValue] = React.useState<SearchSuggestionItem | null>(
        null
    );
    const [inputValue, setInputValue] = React.useState<string>('');
    const [hasMounted, setHasMounted] = React.useState(false);

    const { data, isFetching, isError } = useSearchSuggestionsQuery(inputValue.trim());

    const getIsDataEmpty = () => {
        const hasInputValue = inputValue.trim().length !== 0;

        // if (isError || isFetching) return false;

        if (hasInputValue && (!data || !data.length)) {
            return true;
        }

        const flattenedData = data?.flatMap((group) => group.items) ?? [];

        return Boolean(hasInputValue && flattenedData.length === 0);
    };

    const getStatus = () => {
        if (isError) {
            return 'error';
        }

        if (isFetching) {
            return 'fetching';
        }

        return null;
    };

    const handleItemClick = (item: SearchSuggestionItem) => {
        trackHomepageMapEvent(`Searched for: ${item.text}`);
        addRecentSearch(item);

        // States use bounding boxes for the map search
        if (item.lat_ne && item.lng_ne && item.lat_sw && item.lng_sw) {
            setFilters({
                locationId: item.id,
                lat: null,
                lng: null,
                location: item.location,
                latSw: Number(item.lat_sw),
                lngSw: Number(item.lng_sw),
                latNe: Number(item.lat_ne),
                lngNe: Number(item.lng_ne),
            });
        }
        // Everything else uses the lat/lng
        else {
            setFilters({
                locationId: item.id,
                lat: Number(item.lat),
                lng: Number(item.lng),
                location: item.location,
                latSw: null,
                lngSw: null,
                latNe: null,
                lngNe: null,
            });
        }

        collapseAutocomplete({ shouldFocusTrigger: true });
    };

    React.useEffect(() => {
        setHasMounted(true);
    }, []);

    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target;
            if (!(target instanceof Node)) return;
            const insideContainer = containerRef.current?.contains(target);
            const insidePopup = popupRef.current?.contains(target);
            if (!insideContainer && !insidePopup) {
                collapseAutocomplete();
            }
        }
        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [collapseAutocomplete]);

    /**
     * Collapse when focus moves outside the combobox (e.g. Tab to another control).
     * Mirrors click-outside behavior for keyboard and assistive-tech users.
     */
    React.useEffect(() => {
        function handleFocusIn(event: FocusEvent) {
            const target = event.target;
            if (!(target instanceof Node)) return;
            const insideContainer = containerRef.current?.contains(target);
            const insidePopup = popupRef.current?.contains(target);
            if (!insideContainer && !insidePopup) {
                collapseAutocomplete({ shouldFocusTrigger: false });
            }
        }
        document.addEventListener('focusin', handleFocusIn);

        return () => document.removeEventListener('focusin', handleFocusIn);
    }, [collapseAutocomplete]);

    React.useEffect(() => {
        if (inputRef.current) {
            inputRef.current.select();
        }
    }, []);

    return (
        <Combobox.Root
            items={!inputValue.trim() ? emptySearchContents : data}
            itemToStringLabel={(item) => item?.text ?? ''}
            value={selectedValue}
            onValueChange={setSelectedValue}
            inputValue={inputValue}
            onInputValueChange={setInputValue}
            defaultOpen={true}
            filter={null}
        >
            <div ref={containerRef} className={styles.LocationsCombobox}>
                <UIcon name="map-pin" size={16} className={styles.searchIcon} />

                <div className={styles.inputWrap}>
                    <Combobox.Input
                        ref={inputRef}
                        autoFocus={true}
                        className={styles.Input}
                        placeholder="Search for a location ..."
                        aria-label="Search for locations"
                        onKeyDown={(event) => {
                            if (event.key === 'Escape') {
                                event.preventDefault();

                                // on esc, clear the input if it has a value
                                // otherwise, collapse the search group
                                if (inputValue.trim().length > 0) {
                                    setInputValue('');
                                } else {
                                    collapseAutocomplete({ shouldFocusTrigger: true });
                                    return;
                                }
                            }

                            if (event.key === 'Enter' && data && data.length > 0) {
                                event.preventDefault();
                                const flattenedData = data.flatMap(
                                    (group) => group.items
                                );

                                if (flattenedData.length > 0) {
                                    const firstItem = flattenedData[0];

                                    setInputValue(firstItem.text);
                                    setSelectedValue(firstItem);
                                    handleItemClick(firstItem);
                                }
                            }
                        }}
                    />
                    <Button
                        className={cx(styles.clearButton, {
                            [styles.hasMounted]: hasMounted,
                        })}
                        onClick={() => {
                            setInputValue('');
                            inputRef.current?.focus();
                        }}
                        aria-label="Clear search input"
                    >
                        <UIcon name="x" size={14} />
                    </Button>
                </div>

                <Button
                    className={cx(styles.cancelButton, {
                        [styles.hasMounted]: hasMounted,
                    })}
                    onClick={() => {
                        collapseAutocomplete({ shouldFocusTrigger: true });
                    }}
                >
                    <UIcon
                        name="arrow-left"
                        size={16}
                        aria-label="Close location picker"
                    />
                </Button>
            </div>

            <Combobox.Portal>
                <div ref={popupRef}>
                    <Combobox.Positioner className={styles.Positioner} sideOffset={8}>
                        <LocationComboboxPopup
                            isEmpty={getIsDataEmpty()}
                            handleItemClick={handleItemClick}
                            status={getStatus()}
                        />
                    </Combobox.Positioner>
                </div>
            </Combobox.Portal>
        </Combobox.Root>
    );
}

interface LocationComboboxPopupProps {
    isEmpty: boolean;
    handleItemClick: (item: SearchSuggestionItem) => void;
    status: React.ReactNode | null;
}

function LocationComboboxPopup({
    isEmpty,
    handleItemClick,
    status,
}: LocationComboboxPopupProps) {
    return (
        <Combobox.Popup className={sharedStyles.Popup}>
            <Combobox.Status>
                {isEmpty && status === 'fetching' ? (
                    <div className={sharedStyles.List}>
                        <div className={styles.Item}>
                            <div className={styles.ItemLabel}>
                                <div className="color-text-03">Searching ...</div>
                            </div>
                        </div>
                    </div>
                ) : status === 'fetching' ? (
                    <span
                        role="status"
                        aria-label="Searching ..."
                        className={styles.tinySpinner}
                    />
                ) : status === 'error' ? (
                    <div className={sharedStyles.List}>
                        <div className={styles.Item}>
                            <div className={styles.ItemLabel}>
                                Error loading suggestions
                            </div>
                        </div>
                    </div>
                ) : null}
            </Combobox.Status>

            <Combobox.Empty>
                {isEmpty && status !== 'fetching' && status !== 'error' ? (
                    <div className={sharedStyles.List}>
                        <div className={styles.Item}>
                            <div className={styles.ItemLabel}>
                                <span className="color-text-03">No results found</span>
                            </div>
                        </div>
                    </div>
                ) : null}
            </Combobox.Empty>

            <Combobox.List className={sharedStyles.List}>
                {(group: SearchSuggestionGroup) => {
                    return (
                        <Combobox.Group
                            key={
                                group.items.map((i) => i.id).join('|') || group.groupType
                            }
                            items={group.items}
                            className={cx(styles.Group, {
                                [styles.locationsGroup]: group.groupType === 'locations',
                                [styles.inventoryGroup]: group.groupType === 'inventory',
                            })}
                        >
                            <ScrollArea.Root className={styles.ListArea}>
                                <ScrollArea.Viewport className={styles.ListViewport}>
                                    <ScrollArea.Content className={styles.ListContent}>
                                        {group.groupType === 'recent' ? (
                                            <Combobox.GroupLabel
                                                className={styles.GroupLabel}
                                            >
                                                Recent Searches
                                            </Combobox.GroupLabel>
                                        ) : group.groupType === 'suggestions' ? (
                                            <Combobox.GroupLabel
                                                className={styles.GroupLabel}
                                            >
                                                Fun places to explore
                                            </Combobox.GroupLabel>
                                        ) : group.groupType === 'inventory' ? (
                                            <Combobox.GroupLabel
                                                className={styles.GroupLabel}
                                            >
                                                Search around these facilities or
                                                activities
                                            </Combobox.GroupLabel>
                                        ) : null}
                                        <Combobox.Collection>
                                            {(item: SearchSuggestionItem) => {
                                                return (
                                                    <ComboboxItem
                                                        key={item.id}
                                                        item={item}
                                                        handleItemClick={handleItemClick}
                                                    />
                                                );
                                            }}
                                        </Combobox.Collection>
                                    </ScrollArea.Content>
                                </ScrollArea.Viewport>
                                <ScrollArea.Scrollbar className={styles.Scrollbar}>
                                    <ScrollArea.Thumb className={styles.ScrollbarThumb} />
                                </ScrollArea.Scrollbar>
                            </ScrollArea.Root>
                        </Combobox.Group>
                    );
                }}
            </Combobox.List>
        </Combobox.Popup>
    );
}

interface ComboboxItemProps {
    item: SearchSuggestionItem;
    handleItemClick: (item: SearchSuggestionItem) => void;
}

function ComboboxItem({ item, handleItemClick }: ComboboxItemProps) {
    return (
        <Combobox.Item
            value={item}
            className={styles.Item}
            onClick={() => {
                handleItemClick(item);
            }}
        >
            <div className={styles.ItemLabel}>
                <IconBadge iconName={item.icon} hasInnerBg={item.icon !== 'pin'} />
                <div className={styles.ItemTextGroup}>
                    <div className={styles.ItemName}>{item.text}</div>
                    {item.description ? (
                        <div className={styles.ItemDescription}>{item.description}</div>
                    ) : null}
                </div>
            </div>
        </Combobox.Item>
    );
}
